// tipic.ortodoxia.org: the perpetual calendar of the Typikon of St Sava, drawn from api.ortodoxia.org/v1/tipic. Same
// markup and stylesheet as the dalvalem page it replaces, so it looks the same; the computing happens in the API.
// Addresses: #2026-10-01 (old style) and #nou-2026-10-01 (new style), as on dalvalem.

import type {
  BookService, GltWeek, Link, TipicChapter, TipicDay, TipicMonth, TipicSearch,
} from "../../src/worker/api/contract.ts";
import { gregorianDinIulian, iulianDinGregorian, paschaIulian, zileInLuna } from "../../src/worker/engine/calendar.ts";
import type { YMD } from "../../src/worker/engine/types.ts";

const API = new URLSearchParams(location.search).get("api") ?? "https://api.ortodoxia.org/v1/tipic";
const DALVALEM = "https://dalvalem.imcu.ro";

const LUNI = ["", "ianuarie", "februarie", "martie", "aprilie", "mai", "iunie", "iulie", "august", "septembrie",
  "octombrie", "noiembrie", "decembrie"];
const ZILE = ["Duminică", "Luni", "Marți", "Miercuri", "Joi", "Vineri", "Sâmbătă"];
const ZILE_S = ["Du", "Lu", "Ma", "Mi", "Jo", "Vi", "Sâ"];
const TABURI: [string, string][] = [
  ["vecernie", "Vecernie"], ["pavecernita", "Pavecerniță"], ["miezonoptica", "Miezonoptică"], ["utrenie", "Utrenie"],
  ["ceasuri", "Ceasuri"], ["liturghie", "Liturghie"], ["insemnare", "Însemnare"], ["randuiala", "Rânduială"],
  ["inceput", "Începutul zilei"],
];
const SEMN_CLS: Record<string, string> = {
  rand: "semn-rand", sase: "semn-sase", doxologie: "semn-dox", polieleu: "semn-pol", priveghere: "semn-priv", praznic: "semn-praz",
};
const CAPITOLE = ["11", "14", "19", "21", "22", "23", "35", "49"];
const NUME_SECT: Record<string, string> = { "vecernia-mica": "Vecernia mică", vecernie: "Vecernia", pavecernita: "Pavecernița",
  miezonoptica: "Miezonoptica", utrenie: "Utrenia", ceasuri: "Ceasurile", liturghie: "Liturghia" };
const NUME_SECT_GR: Record<string, string> = { "vecernia-mica": "Μικρὸς Ἑσπερινός", vecernie: "Ἑσπερινός", pavecernita: "Ἀπόδειπνον",
  miezonoptica: "Μεσονυκτικόν", utrenie: "Ὄρθρος", ceasuri: "Ὧραι", liturghie: "Λειτουργία" };

interface State {
  an: number;
  luna: number;
  zi: number;
  nou: boolean;
  tab: string;
  potrivesteTab: boolean;
  q: string;
  capitol: string;
  metoda: boolean;
}

const state: State = { an: 2026, luna: 1, zi: 1, nou: false, tab: "vecernie", potrivesteTab: true, q: "", capitol: "", metoda: false };

// --- data ---

const cache: Record<string, Promise<unknown>> = {};

function get<T>(path: string): Promise<T> {
  cache[path] ??= fetch(API + path).then((r) => {
    if (!r.ok) throw new Error(`${path}: ${r.status}`);
    return r.json();
  }).catch((e: unknown) => {
    delete cache[path];
    throw e;
  });
  return cache[path] as Promise<T>;
}

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (an: number, luna: number, zi: number) => `${String(an).padStart(4, "0")}-${pad(luna)}-${pad(zi)}`;
const style = () => (state.nou ? "new" : "old");
const ymd = (s: string): YMD => {
  const [an, luna, zi] = s.split("-").map(Number) as [number, number, number];
  return { an, luna, zi };
};

/** Today in Bucharest, as a civil date of the chosen calendar. */
function azi(): YMD {
  const [an, luna, zi] = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest" }).format(new Date()).split("-").map(Number) as [number, number, number];
  return state.nou ? { an, luna, zi } : iulianDinGregorian(an, luna, zi);
}

// --- small renderers ---

const esc = (s: unknown) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
const formatData = (d: YMD) => `${d.zi} ${LUNI[d.luna]} ${d.an}`;
const link = (l: Link) => `<a href="${esc(l.url)}" target="_blank" rel="noopener">${esc(l.title)}</a>`;

function durataText(sec: number): string {
  if (!sec) return "";
  const t = Math.round(sec / 60);
  const h = Math.floor(t / 60);
  const m = t % 60;
  return h ? `${h} h ${pad(m)} min` : `${m} min`;
}

/** The section text with its mid-text headings in bold. */
function textCuTitluri(text: string, headings: { start: number; end: number }[]): string {
  let out = "";
  let i = 0;
  for (const h of headings) {
    out += esc(text.slice(i, h.start)) + `<b class="subtitlu">${esc(text.slice(h.start, h.end))}</b>`;
    i = h.end;
  }
  return out + esc(text.slice(i));
}

/** The office tab that has text for the day: the preferred one if the moveable chapter (else any) has it. */
function tabCuText(v: TipicDay, preferat: string): string {
  const mobil = v.blocks.filter((b) => b.kind !== "Minei").flatMap((b) => b.sections.map((s) => s.id));
  const toate = v.blocks.flatMap((b) => b.sections.map((s) => s.id));
  if (mobil.length) return mobil.includes(preferat) ? preferat : mobil[0]!;
  return toate.includes(preferat) ? preferat : toate[0] ?? preferat;
}

// --- toolbar: month, style, year, Pascha, search, chapters ---

function htmlUnelte(v: TipicDay, cautare: TipicSearch | null, capitol: TipicChapter | null): string {
  const p = paschaIulian(state.an);
  const g = gregorianDinIulian(p.an, p.luna, p.zi);
  let tabel = "";
  for (let an = 2020; an <= 2040; an++) {
    const r = paschaIulian(an);
    const rg = gregorianDinIulian(r.an, r.luna, r.zi);
    tabel += `<tr${an === state.an ? ' class="rand-an"' : ""}><td>${an}</td><td>${esc(formatData(r))}</td><td>${esc(formatData(rg))}</td>`
      + `<td>${r.a}</td><td>${r.b}</td><td>${r.c}</td><td>${r.d}</td><td>${r.e}</td></tr>`;
  }
  const butoane = CAPITOLE.map((n) =>
    `<button type="button" data-act="capitol" data-n="${n}"${state.capitol === n ? ' aria-pressed="true"' : ""}>Cap. ${n}</button>`).join("");
  const luna = LUNI[state.luna]!;
  return `<div class="bara">`
    + `<button type="button" data-act="luna" data-d="-1" aria-label="Luna trecută">‹</button>`
    + `<h2>${esc(luna.charAt(0).toUpperCase() + luna.slice(1))} ${state.an}</h2>`
    + `<button type="button" data-act="luna" data-d="1" aria-label="Luna viitoare">›</button>`
    + `<button type="button" data-act="azi">Azi</button>`
    + `<div class="stil" role="group" aria-label="Calendarul">`
    + `<button type="button" data-act="stil" data-nou="0" aria-pressed="${!state.nou}" title="Iulian, ca în Sfântul Munte">Stil vechi</button>`
    + `<button type="button" data-act="stil" data-nou="1" aria-pressed="${state.nou}" title="Calendarul îndreptat al Bisericii Ortodoxe Române">Stil nou · România</button>`
    + `</div>`
    + `<label class="an">An <input id="an" inputmode="numeric" value="${state.an}"></label>`
    + `</div>`
    // One Sunday for the whole Orthodox Church: the two calendars only give it different numbers.
    + `<p class="pasti">Sfintele Paști ${state.an}: `
    + (state.nou
      ? `<b>duminică, ${esc(formatData(g))}</b> (${p.zi} ${LUNI[p.luna]} pe stil vechi)`
      : `<b>duminică, ${esc(formatData(p))}</b> (${g.zi} ${LUNI[g.luna]} pe stil nou)`)
    + ` — aceeași zi în toată Biserica Ortodoxă.`
    + (v.pascha.offset === null ? "" : ` Ziua aleasă e la ${v.pascha.offset} zile față de Paștile din ${v.pascha.year}.`)
    + `</p>`
    + `<div class="caut"><label for="cautare">Caută un sfânt în minei</label>`
    + `<input id="cautare" value="${esc(state.q)}" autocomplete="off" placeholder="Sava, Casian, Lazăr">`
    + htmlCautare(cautare) + `</div>`
    + `<div class="cap-butoane" role="group" aria-label="Capitole">${butoane}</div>`
    + (capitol ? `<article class="capitol" id="capitol"><h3>Capitolul ${capitol.number}. ${esc(capitol.title)}</h3><div class="text">${esc(capitol.text)}</div></article>` : "")
    + `<details class="metoda"${state.metoda ? " open" : ""}><summary>Metoda de calcul a Paștilor</summary>`
    + `<p>Pentru anul ${p.an}: a = ${p.a}, b = ${p.b}, c = ${p.c}, d = ${p.d}, e = ${p.e}. `
    + `a = anul mod 19, b = anul mod 4, c = anul mod 7, d = (19a + 15) mod 30, e = (2b + 4c + 6d + 6) mod 7. `
    + `Dacă d + e &lt; 10, Paștile cad în ${22 + p.d + p.e} martie; altfel în ${p.d + p.e - 9} aprilie, `
    + `fără alte excepții: pe stil vechi d nu trece de 28, deci Paștile cad între 22 martie și 25 aprilie. Data de stil nou se scoate prin numărul zilei iuliene, nu printr-un adaos fix de 13 zile.</p>`
    + `<table><thead><tr><th>An</th><th>Stil vechi</th><th>Aceeași zi pe stil nou</th><th>a</th><th>b</th><th>c</th><th>d</th><th>e</th></tr></thead><tbody>${tabel}</tbody></table></details>`;
}

function htmlCautare(r: TipicSearch | null): string {
  if (!r) return "";
  if (!r.results.length) return `<p class="rez-gol">Niciun sfânt cu acest nume în minei.</p>`;
  return `<ul class="rez">` + r.results.map((g) =>
    `<li><button type="button" data-act="sfant" data-luna="${g.month}" data-zi="${g.day}">${g.day} ${esc(LUNI[g.month])} — ${esc(g.title)}</button></li>`).join("") + `</ul>`;
}

// --- month grid and day ---

function htmlCorp(m: TipicMonth, v: TipicDay, glt: GltWeek | null): string {
  const a = azi();
  const wd1 = m.days[0]?.weekday ?? 0;
  let celule = `<span class="gol"></span>`.repeat(wd1);
  for (const d of m.days) {
    const { zi } = ymd(d.date);
    const alt = ymd(d.otherStyle);
    const cls = ["zi"];
    if (d.weekday === 0) cls.push("duminica");
    if (state.an === a.an && state.luna === a.luna && zi === a.zi) cls.push("azi");
    if (zi === state.zi) cls.push("aleasa");
    celule += `<button type="button" class="${cls.join(" ")}" data-act="zi" data-zi="${zi}"${zi === state.zi ? ' aria-current="date"' : ""}>`
      + `<span class="num">${zi}</span>`
      + (d.rank.glyph ? `<span class="marc ${SEMN_CLS[d.rank.code] ?? ""}" title="${esc(d.rank.label)}">${d.rank.glyph}</span>` : "")
      + `<span class="sf">${esc(d.label)}</span>`
      + `<span class="nou" title="${state.nou ? "stil vechi" : "stil nou"}">${alt.zi}.${alt.luna}</span></button>`;
  }
  const capete = ZILE_S.map((z, i) => `<span${i === 0 ? ' class="rosu"' : ""}>${z}</span>`).join("");
  return `<div class="corp"><div class="grila-wrap"><div class="capete">${capete}</div><div class="grila">${celule}</div>`
    + (state.luna === 2 ? `<p class="feb">Prefața spune că februarie are 28 de zile. Casian Romanul, ziua 29, se arată în anii bisecți${state.nou ? " ai stilului nou" : ""}.</p>` : "")
    + `</div>` + htmlZi(v, glt) + `</div>`;
}

function htmlCatismeAudio(v: TipicDay): string {
  const rand = (slujba: string, nums: number[], nota = "") => {
    if (!nums.length) return "";
    const una = nums.map((n) => `<button type="button" class="cat-b" data-act="cat-una" data-slujba="${slujba}" data-n="${n}" title="pornește doar catisma ${n}">▶ ${n}</button>`).join("");
    const toate = nums.length > 1
      ? `<button type="button" class="cat-b cat-toate" data-act="cat-toate" data-slujba="${slujba}" data-nums="${nums.join(",")}" title="una după alta, fără pauză">▶ toate, fără pauză</button><span class="cat-sau">sau câte una:</span>`
      : "";
    return `<div class="cat-rand"><span class="cat-slujba">${slujba}${nota ? ` <small>(${nota})</small>` : ""}</span>${toate}${una}</div>`;
  };
  let alta = `<option value="">altă catismă…</option>`;
  for (let n = 1; n <= 20; n++) alta += `<option value="${n}">catisma ${n}</option>`;
  let l = "ro";
  try { if (localStorage.getItem("tipic-cat-limba") === "gr") l = "gr"; } catch { /* private mode */ }
  const lb = (k: string, eti: string, titlu: string) =>
    `<button type="button" class="cat-l${l === k ? " on" : ""}" data-act="cat-limba" data-l="${k}" aria-pressed="${l === k}" title="${titlu}">${eti}</button>`;
  return `<div class="cat-audio"><div class="cat-cap">🎧 Psaltirea citită<span class="cat-limba">`
    + lb("ro", "Română · IPS Teofan", "Psaltirea citită de IPS Teofan") + lb("gr", "Ελληνικά", "Psaltirea în limba greacă") + `</span></div>`
    + rand("Utrenie", v.psalter.matins) + rand("Vecernie", v.psalter.vespers, v.psalter.vespersNote) + rand("Miezonoptică", v.psalter.midnightOffice)
    + `<div class="cat-rand"><select data-act="cat-alta" aria-label="altă catismă">${alta}</select></div></div>`;
}

function htmlCitiri(v: TipicDay): string {
  const c = v.readings;
  if (!c) return "";
  if ("missing" in c) return `<div><dt>Apostol, Evanghelie</dt><dd>${esc(c.missing)}</dd></div>`;
  const rand = (nume: string, x: { ref: string; text: string | null } | null) => x
    ? `<div><dt>${nume}</dt><dd><span class="cit-ref">${esc(x.ref)}</span>`
      + (x.text ? `<details class="cit-text"><summary>Textul</summary><div>${esc(x.text)}</div></details>` : "") + `</dd></div>`
    : "";
  return rand("Apostol", c.apostle) + rand("Evanghelie", c.gospel)
    + `<div class="cit-sursa"><dt></dt><dd>${esc(c.basis ? c.basis + " " : "")}Din <a href="${esc(v.links.calendarAthos)}" target="calendar-athos">Calendarul Athos</a> (Schitul Lacu).</dd></div>`;
}

function htmlLacu(v: TipicDay): string {
  const lista = v.services.recordings;
  if (!lista.length) return "";
  return `<section class="lacu" id="lacu"><h3>🎧 Slujba zilei, înregistrată</h3>` + lista.map((e) => {
    const total = e.files.reduce((t, f) => t + (f.seconds || 0), 0);
    const cap = `<p class="lacu-titlu">${esc(e.title)} <small>· ${esc(e.source)}${total ? " · " + durataText(total) : ""}`
      + ` · <a href="${esc(e.folder)}" target="_blank" rel="noopener">folderul</a></small></p>`;
    if (e.files.length > 6 && e.files.every((f) => f.name)) {
      const piese = e.files.map((f, i) => `<li><button type="button" class="lacu-p${i ? "" : " on"}" data-act="lacu-piesa" data-url="${esc(f.url)}">${esc(f.name)}</button>`
        + (f.seconds ? ` <small>${durataText(f.seconds)}</small>` : "") + `</li>`).join("");
      return `<div class="lacu-slujba">${cap}<div class="lacu-f"><audio class="lacu-player" controls preload="none" src="${esc(e.files[0]!.url)}"></audio></div>`
        + `<details class="lacu-lista"><summary>Piesele (${e.files.length}), una după alta</summary><ol>${piese}</ol></details></div>`;
    }
    const parti = e.files.map((f, i) => {
      const eti = (e.files.length === 1 ? "Înregistrarea" : f.name || `Partea ${i + 1}`) + (f.seconds ? " · " + durataText(f.seconds) : "");
      return `<div class="lacu-f"><span class="lacu-eti">${esc(eti)}</span><audio controls preload="none" src="${esc(f.url)}"></audio>`
        + `<a href="${esc(f.url)}" download title="descarcă mp3">⬇</a></div>`;
    }).join("");
    return `<div class="lacu-slujba">${cap}${parti}</div>`;
  }).join("") + `</section>`;
}

function htmlCarte(s: BookService, gr: boolean): string {
  const nume = gr ? { ...NUME_SECT_GR, mare: "Μέγας Ἑσπερινός" } : { ...NUME_SECT, mare: "Vecernia mare" };
  const sect = s.sections.map((x, i) => {
    const n = x.id === "vecernie" && i && s.sections[i - 1]!.id === "vecernia-mica" ? nume.mare : (nume as Record<string, string>)[x.id] ?? x.heading;
    return x.url ? `<a href="${esc(x.url)}" target="_blank" rel="noopener">${n}</a>` : n;
  }).join(" · ");
  return `<div class="sc-it"${gr ? ' lang="el"' : ""}><a href="${esc(s.url)}" target="_blank" rel="noopener">${esc(s.title)}</a>`
    + (sect ? `<span class="sc-sect">${sect}</span>` : "") + `</div>`;
}

function htmlSlujbe(v: TipicDay, glt: GltWeek | null): string {
  const { ro, gr, order } = v.services;
  const carti = [...new Set([...ro, ...gr].map((s) => s.book))];
  const cel = (lista: BookService[], greaca: boolean) =>
    `<div class="sc-col" data-l="${greaca ? "gr" : "ro"}">${lista.length ? lista.map((s) => htmlCarte(s, greaca)).join("") : '<span class="sc-gol">—</span>'}</div>`;
  const randuri = carti.map((c) => `<div class="sc-rand"><span class="sc-carte">${esc(c)}</span>`
    + cel(ro.filter((s) => s.book === c), false) + cel(gr.filter((s) => s.book === c), true) + `</div>`).join("");
  const randuri2 = order.map((r) => {
    const l = r.ro.map(link).join(" · ");
    const g = r.gr.map(link).join(" · ");
    return `<div class="sc-rand"><span class="sc-carte">${esc(r.office)}</span><div class="sc-col" data-l="ro">${l}`
      + (r.note ? (l ? ` <small class="sc-nota">(${esc(r.note)})</small>` : `<small class="sc-nota">${esc(r.note)}</small>`) : "")
      + `</div><div class="sc-col" data-l="gr" lang="el">${g || '<span class="sc-gol">—</span>'}</div></div>`;
  }).join("");
  const cap = `<div class="sc-rand sc-cap"><span></span><span>Română · ortodox007</span><span>Ελληνικά · glt.goarch.org</span></div>`;
  const athos = athosSaptamana(v, glt);
  const { luna, zi } = ymd(v.date);
  return `<section class="slujbe-carti cu-gr"><h3>Slujbele zilei în cărțile de slujbă</h3>`
    + (athos ? `<p class="sc-athos" lang="el">☦ <a href="${esc(athos)}" target="_blank" rel="noopener">Toată slujba zilei în greacă, alcătuită după tipicul aghioritic</a>`
      + ` <small>(glt.goarch.org, numai pentru săptămâna în curs${state.nou ? "" : ", stil vechi"})</small></p>` : "")
    + (randuri ? `<div class="sc-tab">${cap}${randuri}</div>` : `<p class="lipsa">Pentru ziua aceasta nu e o slujbă cu ancoră proprie în arhivă.</p>`)
    + `<h4 class="sc-sub">Rânduiala slujbelor, cu textul întreg</h4><div class="sc-tab">${cap}${randuri2}</div>`
    + `<p class="sc-sursa">Textele românești sunt din arhiva ortodox007 (Mineiele, Octoihul Mare, Triodul, Penticostarul), `
    + `aceeași din care <a href="${DALVALEM}/vieti-audio/?m=${luna}&d=${zi}" target="_blank" rel="noopener">Viețile Sfinților audio</a> ia slujba din Minei. `
    + `Ceaslovul e din aceeași arhivă, iar Liturghierele sunt cel pastoral (BOR) și cel misionar (Ierom. Petru Pruteanu). `
    + `Cele grecești sunt de pe <a href="https://glt.goarch.org/" target="_blank" rel="noopener">glt.goarch.org</a> (Arhiepiscopia Greacă a Americii); „Ἑσπερινός”, „Ὄρθρος”, „Λειτουργία” sar la titlul acela din pagină.</p></section>`;
}

/** glt.goarch.org's whole service of the day, only within a week of today (its page titles have no year). */
function athosSaptamana(v: TipicDay, glt: GltWeek | null): string | null {
  const url = glt?.[v.style][v.date.slice(5)];
  if (!url) return null;
  const today = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Bucharest" }).format(new Date());
  return Math.abs(Date.parse(v.gregorian) - Date.parse(today)) <= 7 * 86_400_000 ? url : null;
}

function htmlZi(v: TipicDay, glt: GltWeek | null): string {
  const bucati: string[] = [];
  for (const b of v.blocks) {
    const ale = b.sections.filter((s) => s.id === state.tab);
    ale.forEach((s, n) => {
      bucati.push(`<section class="bucata"><p class="fel">${esc(b.kind)}${ale.length > 1 ? ` (${n + 1})` : ""}</p>`
        + (b.coverage ? `<p class="acoperire">${esc(b.coverage)}</p>` : "")
        + `<h3>${esc(b.title)}</h3>`
        + (s.variant ? `<p class="varianta">${esc(s.variant)}</p>` : "")
        + `<div class="text">${textCuTitluri(s.text, s.headings)}</div></section>`);
    });
  }
  if (!bucati.length) {
    const au = TABURI.filter(([id]) => v.blocks.some((b) => b.sections.some((s) => s.id === id))).map(([, nume]) => nume);
    let lipsa = state.tab === "pavecernita"
      ? "În textul extras al zilei nu e o secțiune aparte de pavecerniță. Vezi vecernia și însemnarea."
      : "În textul extras al zilei nu e o secțiune cu acest nume.";
    if (au.length) lipsa += ` Textul extras este la: ${au.join(", ")}.`;
    bucati.push(`<p class="lipsa">${lipsa}</p>`);
  }
  const date = ymd(v.date);
  const alta = ymd(state.nou ? v.julian : v.gregorian);
  const taburi = TABURI.map(([id, nume]) =>
    `<button type="button" role="tab" data-act="tab" data-tab="${id}"${state.tab === id ? ' aria-selected="true"' : ""}>${nume}</button>`).join("");
  const r = v.rubrics;
  return `<article class="ziua" id="ziua"><p class="wd">${ZILE[v.weekday]}</p><h2>${esc(formatData(date))}</h2>`
    + `<p class="nou-mare">${state.nou ? "stil vechi: " : "stil nou: "}${esc(formatData(alta))}</p>`
    + `<p class="la-athos"><a href="${esc(v.links.calendarAthos)}" target="calendar-athos" title="aceeași zi în Calendarul Athos (Schitul Lacu)">☦ Ziua în Calendarul Athos</a></p>`
    + (v.saint ? `<p class="sfant">${esc(v.saint)}</p>` : "")
    + v.related.map((a) => `<p class="sfant">Tot în această zi: ${esc(a)}</p>`).join("")
    + `<p class="perioada">${esc(v.period)}</p><dl class="fisa">`
    + `<div><dt>Glas</dt><dd>${esc(v.tone.text)}</dd></div>`
    + (r.resurrectionGospel ? `<div><dt>Voscreasna</dt><dd>${esc(r.resurrectionGospel)}</dd></div>` : "")
    + (v.menaionSuppressed
      ? `<div><dt>Semn</dt><dd>Mineiul e lăsat deoparte în această zi, așa că semnul lui nu rânduiește slujba.</dd></div>`
      : `<div><dt>Semn</dt><dd><span class="${SEMN_CLS[v.rank.code] ?? ""}">${esc(v.rank.glyph ? v.rank.glyph + " " : "")}${esc(v.rank.label)}</span>. ${esc(v.rank.description)} Semnul e citit din text, nu înlocuiește rânduiala.</dd></div>`)
    + (r.polyeleos ? `<div><dt>Polieleu</dt><dd>${esc(r.polyeleos)}</dd></div>` : "")
    + `<div><dt>Post</dt><dd>${esc(r.fasting)}</dd></div>`
    + `<div><dt>Catisme</dt><dd>${esc(r.kathismata)}${htmlCatismeAudio(v)}</dd></div>`
    + `<div><dt>Catavasie</dt><dd>${esc(r.katavasia)}</dd></div>`
    + `<div><dt>Mai cinstită</dt><dd>${esc(r.theotokion)}</dd></div>`
    + `<div><dt>Liturghie</dt><dd>${esc(r.liturgy)}</dd></div>`
    + htmlCitiri(v) + `</dl>` + htmlLacu(v)
    + `<ul class="note">${v.notes.map((n) => `<li>${esc(n)}</li>`).join("")}</ul>`
    + `<div class="taburi" role="tablist">${taburi}</div><div class="citire">${bucati.join("")}</div>`
    + htmlSlujbe(v, glt) + `</article>`;
}

// --- drawing and navigation ---

let desen = 0;

async function deseneaza(): Promise<void> {
  const unelte = document.getElementById("unelte")!;
  const corp = document.getElementById("corp")!;
  const eu = ++desen;
  const data = iso(state.an, state.luna, state.zi);
  try {
    const [m, v, cautare, capitol, glt] = await Promise.all([
      get<TipicMonth>(`/months/${data.slice(0, 7)}?style=${style()}`),
      get<TipicDay>(`/days/${data}?style=${style()}`),
      state.q.replace(/[^\p{L}\p{N}]/gu, "").length >= 2 ? get<TipicSearch>(`/search?q=${encodeURIComponent(state.q)}`) : null,
      state.capitol ? get<TipicChapter>(`/chapters/${state.capitol}`) : null,
      get<GltWeek>("/glt-week").catch(() => null),
    ]);
    if (eu !== desen) return;
    if (state.potrivesteTab) {
      state.tab = tabCuText(v, state.tab);
      state.potrivesteTab = false;
    }
    const cautat = document.activeElement?.id === "cautare";
    unelte.innerHTML = htmlUnelte(v, cautare, capitol);
    corp.innerHTML = htmlCorp(m, v, glt);
    document.getElementById("ochi-stil")!.textContent = state.nou ? "România · calendarul îndreptat, stil nou" : "Sfântul Munte · stil vechi";
    if (cautat) {
      const c = document.getElementById("cautare") as HTMLInputElement;
      c.focus();
      c.setSelectionRange(c.value.length, c.value.length);
    }
  } catch (e) {
    if (eu === desen) corp.innerHTML = `<p class="astept">Tipicul nu s-a putut deschide (${esc((e as Error).message)}). Reîncearcă.</p>`;
  }
}

function alege(an: number, luna: number, zi: number, scroll = false): void {
  state.an = an;
  state.luna = luna;
  state.zi = Math.min(zi, zileInLuna(an, luna, state.nou));
  state.potrivesteTab = true;
  const h = `#${state.nou ? "nou-" : ""}${iso(state.an, state.luna, state.zi)}`;
  if (location.hash !== h) history.replaceState(null, "", h);
  void deseneaza();
  if (scroll && matchMedia("(max-width: 800px)").matches) document.getElementById("ziua")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function dinAdresa(): boolean {
  const m = /^#(nou-)?(\d{3,4})-(\d{2})-(\d{2})$/.exec(location.hash);
  if (!m) return false;
  const an = Number(m[2]);
  if (an < 1900 || an > 2100) return false;
  state.nou = Boolean(m[1]);
  alege(an, Number(m[3]), Number(m[4]));
  return true;
}

function laClick(ev: MouseEvent): void {
  const t = (ev.target as Element).closest<HTMLElement>("[data-act]");
  if (!t) return;
  const act = t.dataset.act;
  if (act === "zi") alege(state.an, state.luna, Number(t.dataset.zi), true);
  else if (act === "luna") {
    const d = Number(t.dataset.d);
    const luna = state.luna + d;
    const [an, l] = luna < 1 ? [state.an - 1, 12] : luna > 12 ? [state.an + 1, 1] : [state.an, luna];
    if (an >= 1900 && an <= 2100) alege(an, l, state.zi);
  } else if (act === "azi") {
    const a = azi();
    alege(a.an, a.luna, a.zi);
  } else if (act === "stil") {
    state.nou = t.dataset.nou === "1";
    try { localStorage.setItem("tipic-stil", state.nou ? "nou" : "vechi"); } catch { /* private mode */ }
    alege(state.an, state.luna, state.zi);
  } else if (act === "tab") {
    state.tab = t.dataset.tab ?? state.tab;
    void deseneaza();
  } else if (act === "capitol") {
    state.capitol = state.capitol === t.dataset.n ? "" : t.dataset.n ?? "";
    void deseneaza();
  } else if (act === "sfant") {
    state.q = "";
    alege(state.an, Number(t.dataset.luna), Number(t.dataset.zi), true);
  } else if (act === "lacu-piesa") {
    const sl = t.closest(".lacu-slujba");
    const pl = sl?.querySelector<HTMLAudioElement>(".lacu-player");
    if (!sl || !pl) return;
    sl.querySelectorAll(".lacu-p.on").forEach((x) => x.classList.remove("on"));
    t.classList.add("on");
    pl.src = t.dataset.url ?? "";
    void pl.play().catch(() => {});
  }
}

let tastare = 0;

function porneste(): void {
  document.addEventListener("click", laClick);
  document.addEventListener("toggle", (ev) => {
    if ((ev.target as Element).matches?.("details.metoda")) state.metoda = (ev.target as HTMLDetailsElement).open;
  }, true);
  // the next named piece of a recorded service starts when one ends ("ended" does not bubble)
  document.addEventListener("ended", (ev) => {
    const pl = ev.target as HTMLAudioElement;
    if (!pl.classList?.contains("lacu-player")) return;
    const urm = pl.closest(".lacu-slujba")?.querySelector(".lacu-p.on")?.closest("li")?.nextElementSibling?.querySelector<HTMLElement>(".lacu-p");
    urm?.click();
  }, true);
  document.addEventListener("input", (ev) => {
    const t = ev.target as HTMLInputElement;
    if (t.id === "cautare") {
      state.q = t.value;
      clearTimeout(tastare);
      tastare = window.setTimeout(() => void deseneaza(), 250);
    }
  });
  document.addEventListener("change", (ev) => {
    const t = ev.target as HTMLInputElement;
    const an = Number(t.value);
    if (t.id === "an" && an >= 1900 && an <= 2100) alege(an, state.luna, state.zi);
  });
  window.addEventListener("hashchange", () => void dinAdresa());
  if (!dinAdresa()) {
    try { state.nou = localStorage.getItem("tipic-stil") === "nou"; } catch { /* private mode */ }
    const a = azi();
    alege(a.an, a.luna, a.zi);
  }
}

porneste();
